import { useCallback, useState } from 'react'

/**
 * Which rows of a register are ticked.
 *
 * Extracted from `useVehicleSelection` when machinery needed the same thing.
 * There is nothing vehicle-shaped in here — it is a set of ids — and two copies
 * of it would drift on exactly the subtlety `toggleMany` documents below.
 *
 * Deliberately NOT in the URL, unlike a register's filters.
 *
 * The filters belong there because they describe a view somebody may want to
 * share or return to. A selection is a working set — built up over a minute of
 * clicking, consumed once — and putting 40 ids in the address bar would make
 * every checkbox a history entry and the URL unreadable. It survives paging and
 * filtering because it lives above the query, which is the part that matters; it
 * does not survive a reload, and should not.
 *
 * A Set, so toggling 600 rows stays O(1) per click rather than scanning an array.
 */
export function useRowSelection() {
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())

  const toggle = useCallback((id: number) => {
    setSelected((current) => {
      const next = new Set(current)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }, [])

  /**
   * Adds or removes a whole page.
   *
   * Adds unless every id given is already selected, so the header checkbox on a
   * partly-selected page COMPLETES it rather than clearing it — clearing loses
   * work the user did on other pages, and is the more expensive mistake.
   */
  const toggleMany = useCallback((ids: number[]) => {
    setSelected((current) => {
      const next = new Set(current)
      const allPresent = ids.every((id) => next.has(id))

      for (const id of ids) {
        if (allPresent) next.delete(id)
        else next.add(id)
      }

      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  return { selected, toggle, toggleMany, clear }
}
